import React from 'react';
import {HiddenSSR} from '../../elements/hidden';
import Grid from '@material-ui/core/Grid';
import {GridContainer} from '../../elements/grid/GridContainer';
import {AnimatedHeading} from '../../elements/typography/AnimatedHeading';
import {VerticalSpacer} from '../../elements/spacer/VerticalSpacer';
import {AccentColorOptions} from 'epic-ue-theme';
import {Typography} from '../../elements/typography/Typography';
import {Button} from '../../elements/buttons/Button';
import {AnimateOnViewWrap} from '../../wraps/animation';
import {Spotlight} from '../../wraps';

import styled from 'styled-components';

const getRadialGradient = (from, to) =>
    `radial-gradient(100% 185.14% at 100% 100%, ${from} 0%, ${to} 100%)`;

const VerticalLine = styled.div`
    width: 3px;
    margin: 0 auto;
    height: 60px;
    ${(p) => {
        const colors: string[] = [];
        const {gradient} = p.theme.palette;
        for (const color in gradient) {
            if (Object.prototype.hasOwnProperty.call(gradient, color)) {
                colors.push(`
                    &.${color} {
                        background-image: ${getRadialGradient(
                            gradient[color].from,
                            gradient[color].to
                        )};
                        }
                    }
                `);
            }
        }
        return colors.join('\n');
    }}
`;

const LicensingOptionWrapper = styled.div`
    && {
        text-align: center;
    }
    .paragraph {
        color: ${(p) => p.theme.palette.text.secondary};
        ${(p) => p.theme.breakpoints.up('md')} {
            padding: 0 ${(p) => p.theme.spacing(3)}px;
        }
        ${(p) => p.theme.breakpoints.up('lg')} {
            padding: 0 ${(p) => p.theme.spacing(6)}px;
        }
    }
`;

const LicensingSectionWrapper = styled.div`
    color: ${(p) => p.theme.ifLight(p, p.theme.palette.common.black, p.theme.palette.common.white)};
    .ue-heading {
        h2,
        .eyebrow-wrapper {
            text-align: center;
        }
    }
`;

interface LicensingOptionProps {
    title: string;
    subtitle: string;
    caption?: string;
    description: string;
    ctaText?: string;
    href: string;
    className?: string;
    accentColor?: AccentColorOptions;
}

const LicensingOption = (props: LicensingOptionProps): JSX.Element | null => {
    const {
        title,
        subtitle,
        caption,
        description,
        ctaText = 'Learn More',
        href,
        className,
        accentColor = 'sky'
    } = props;

    return (
        <LicensingOptionWrapper className={className}>
            <Typography variant="h2">{title}</Typography>
            <VerticalSpacer variant="custom" spacing={3} downSmSpacing={2} />
            <VerticalLine className={`vertical-line ${accentColor}`} />
            <VerticalSpacer variant="custom" spacing={3} downSmSpacing={2} />
            <Typography variant="h4" big>
                {subtitle}
            </Typography>
            {caption && (
                <>
                    <VerticalSpacer variant="custom" spacing={0.5} />
                    <Typography variant="p" caption>
                        {caption}
                    </Typography>
                </>
            )}
            <VerticalSpacer variant="eyebrow" />
            <Typography variant="p">{description}</Typography>
            {href && (
                <>
                    <VerticalSpacer variant="heading" />
                    <Button type="secondary" color={accentColor} href={href}>
                        {ctaText}
                    </Button>
                </>
            )}
        </LicensingOptionWrapper>
    );
};

export interface LicensingSectionProps {
    heading?: string;
    className?: string;
    options?: LicensingOptionProps[];
    accentColor?: AccentColorOptions;
    hidden?: boolean;
    hideBottomSectionPadding?: boolean;
}

export const LicensingSection = (props: LicensingSectionProps): JSX.Element | null => {
    const {heading, className, options = [], accentColor, hidden, hideBottomSectionPadding} = props;

    if (hidden) return null;

    const optionsList = options.map((option, index) => {
        return (
            <Grid item xs={12} md={4} key={`${option.title}-${index}-outer`}>
                <LicensingOption
                    key={`${option.title}-${index}-inner`}
                    title={option.title}
                    subtitle={option.subtitle}
                    caption={option.caption}
                    description={option.description}
                    ctaText={option.ctaText}
                    href={option.href}
                    className={option.className}
                    accentColor={accentColor}
                />
                <HiddenSSR mdUp>
                    {!hideBottomSectionPadding && <VerticalSpacer variant="custom" spacing={7} />}
                </HiddenSSR>
            </Grid>
        );
    });

    return (
        <LicensingSectionWrapper className={className}>
            <Spotlight
                accentColor={accentColor}
                variant="big"
                shape="oval"
                tilt="right"
                offsetRatio={[0.0, 0.2]}
                offsetRatioDown={[0.2, 0.7]}>
                <GridContainer>
                    <AnimatedHeading heading={heading} />
                    <VerticalSpacer variant="section" />
                    <AnimateOnViewWrap translateY={30}>
                        <Grid container>{optionsList}</Grid>
                    </AnimateOnViewWrap>
                </GridContainer>
            </Spotlight>
        </LicensingSectionWrapper>
    );
};
